import React from 'react';
import Hero from '@/app/components/hotel-detail/Hero';
import HotelPageFacilitiesSection from '@/app/components/hotel-detail/HotelPageFacilitiesSection';
import { HotelPageRoomSection } from '@/app/components/hotel-detail/HotelPageRoomSection';
import { AdsFooter } from '@/app/components/hotel-detail/AdsFooter';
import { DebugPanel } from '@/app/components/hotel-detail/DebugPanel';

import HotelPageOverviewSection from '@/app/components/hotel-detail/HotelPageOverviewSection';
import type { BrandedProps } from '@/app/hotel-detail/createHotelDetailData';
import { dynamicImportCatch } from '@/utils/appUtilsClient.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import dynamic from 'next/dynamic';
import HotelPagePoliciesSection from '@/app/components/hotel-detail/HotelPagePoliciesSection.tsx';
import useInitialScrollbarWidth from '@/app/common/hooks/useInitialScrollbarWidth.ts';
import { useReportHotelNoAvailability } from '@/app/components/hotel-detail/hooks/useReportHotelNoAvailability';
import { Skyscrappers } from '@/app/common/biff/hotelDetailPage/adSlots.tsx';
import HotelPageBeforeYouBookSection from './HotelPageBeforeYouBookSection';
// import { useFetchGuestSummarizedReviews } from './hooks/useFetchGuestSummarizedReviews';
import HotelPageEmissionsSection from './HotelPageEmissionsSection';
import customCss from './css/customCss';

const HotelMapModal = dynamic(() => import('./HotelMapModal').catch(dynamicImportCatch), {
  ssr: true,
});

const HotelProviderSelector = dynamic(() => import('./HotelProviderSelector').catch(dynamicImportCatch), {
  ssr: true,
});

const HotelPageGuestReviewsSection = dynamic(() => import('./HotelPageGuestReviewsSection').catch(dynamicImportCatch), {
  ssr: true,
});

const HotelPageReviewSection = dynamic(() => import('./HotelPageReviewSection').catch(dynamicImportCatch), {
  ssr: true,
});

const HotelPageSimilarHotelsSection = dynamic(
  () => import('./HotelPageSimilarHotelsSection').catch(dynamicImportCatch),
  {
    ssr: true,
  }
);

export type HotelAndRoomsLayoutProps = BrandedProps;

export default function HotelAndRoomsLayout(props: HotelAndRoomsLayoutProps) {
  const {
    initialParams: { atol, tshUrl },
  } = props;
  const { localization } = usePageWrapperData();
  useInitialScrollbarWidth();
  useReportHotelNoAvailability();

  // prefetch
  // useFetchGuestSummarizedReviews();

  /*
   * The searchId must be set to undefined because is not validated in SSR, this value comes from url so
   * we need to validate it before being used in other components. From SSR we have a validated sessionId from handshake
   * and using that sessionId with a wrong searchId can cause errors in endpoint calls.
   * The searchId will be set in the search endpoint call response.
   */
  // const [extendedSearch, setExtendedSearch] = useState<SearchData>({ ...search, searchId: undefined });

  return (
    <div
      css={`
        display: grid;
        grid-template-areas:
          'telesales'
          'hero'
          'debug'
          'aside'
          'partnership-info'
          'plus-info'
          'offers'
          'similar-hotels'
          'aside-reviews'
          'reviews'
          'guest-reviews'
          'amenities'
          'overview'
          'policies'
          'emissions'
          'before-you-book'
          'similar-hotels-bottom'
          'ad-bottom'
          'footer';

        & > * {
          min-width: 0;
        }

        @media ${customCss.breakpoints.xl} {
          /* grid-gap: 0 0.7rem; */
          grid-template-columns: auto 208px fit-content(1200px) 208px auto;
          /* One auto per leading row, so the 1fr stays on the same content row it had before
             the debug row was inserted under the hero. */
          grid-template-rows: auto auto auto auto auto 1fr;
          grid-template-areas:
            '. . telesales ad-skyscrapers .'
            'left aside hero ad-skyscrapers right'
            '. . debug ad-skyscrapers .'
            '. . plus-info ad-skyscrapers .'
            '. . offers ad-skyscrapers .'
            '. . similar-hotels ad-skyscrapers .'
            '. . reviews ad-skyscrapers .'
            '. . guest-reviews ad-skyscrapers .'
            '. . amenities ad-skyscrapers .'
            '. . overview ad-skyscrapers .'
            '. . policies ad-skyscrapers .'
            '. . emissions ad-skyscrapers .'
            '. . before-you-book ad-skyscrapers .'
            '. . similar-hotels-bottom ad-skyscrapers .'
            '. . ad-bottom ad-skyscrapers .'
            '. . footer . .';
        }
      `}
    >
      {localization.isTelesales ? <HotelProviderSelector style={{ gridArea: 'telesales' }} /> : null}
      <Hero style={{ gridArea: 'hero' }} />
      <DebugPanel
        style={{ gridArea: 'debug' }}
        css={`
          margin: 16px;

          @media ${customCss.breakpoints.xl} {
            margin: 16px 0;
          }
        `}
      />
      <Skyscrappers style={{ gridArea: 'ad-skyscrapers' }} />
      <HotelMapModal />
      <HotelPageRoomSection style={{ gridArea: 'offers' }} />
      <HotelPageReviewSection id="reviews" language={localization.lang} style={{ gridArea: 'reviews' }} />
      <HotelPageGuestReviewsSection style={{ gridArea: 'guest-reviews' }} />
      <HotelPageFacilitiesSection id="facilities" style={{ gridArea: 'amenities' }} />
      <HotelPageOverviewSection style={{ gridArea: 'overview' }} />
      <HotelPagePoliciesSection style={{ gridArea: 'policies' }} />
      <HotelPageEmissionsSection style={{ gridArea: 'emissions' }} />
      <HotelPageBeforeYouBookSection style={{ gridArea: 'before-you-book' }} tshUrl={tshUrl} atol={atol} />
      <HotelPageSimilarHotelsSection style={{ gridArea: 'similar-hotels' }} />
      <AdsFooter />
    </div>
  );
}
